import { CanvasSource, LngLat, Map, Point } from 'mapbox-gl';
import { lngLatToPoint, pointToLngLat } from '@/lib/map/mercatorHelpers';
import {
  along,
  bearing,
  length,
  lineString,
  Feature,
  LineString,
  Properties,
} from '@turf/turf';
import store from '@/store';
import { BaseSpriteSource, NXWConfig, SpriteInstance } from '@/types';

export interface SpriteSourceExtended extends BaseSpriteSource {
  image: HTMLCanvasElement;
}

export default class AnimatedMapboxGraphic {
  private config: NXWConfig = store.state.config;

  private spriteSource: SpriteSourceExtended;

  private spriteInstance: SpriteInstance;

  private canvas: HTMLCanvasElement;

  private ctx: CanvasRenderingContext2D;

  private currentSpriteFrame = 0;

  private frameCount = 0;

  private canvasSource!: CanvasSource;

  private routeLineString: Feature<LineString, Properties> | undefined;

  private routeLength: number | undefined;

  private currentRouteStep = 0;

  private currentSpriteSequence: string;

  private isHighFidelity: boolean;

  constructor(
    spriteInstance: SpriteInstance,
    spriteSource: SpriteSourceExtended,
    map: Map,
    isHighFidelity: boolean
  ) {
    this.spriteInstance = spriteInstance;
    this.spriteSource = spriteSource;
    this.currentSpriteSequence = this.spriteInstance.sequence;
    // Randomise the first frame
    this.currentSpriteFrame = Math.floor(
      (this.spriteSource.frameSequences[this.currentSpriteSequence].length -
        1) *
        Math.random()
    );
    this.canvas = this.createCanvas(spriteSource);
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    this.isHighFidelity = isHighFidelity;
    if (spriteInstance.route) {
      this.routeLineString = lineString(spriteInstance.route.coordinates);
      this.routeLength = length(this.routeLineString, { units: 'miles' });
      const firstPoint = lngLatToPoint(
        new LngLat(
          spriteInstance.route.coordinates[0][0],
          spriteInstance.route.coordinates[0][1]
        ),
        this.config.map.mapWidth,
        this.config.map.mapHeight
      );
      this.canvasSource = this.addToMap([firstPoint.x, firstPoint.y], map);
    } else if (spriteInstance.center) {
      this.canvasSource = this.addToMap(spriteInstance.center, map);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private createCanvas(spriteSource: SpriteSourceExtended): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = spriteSource.width + 1;
    canvas.height = spriteSource.height + 1;
    return canvas;
  }

  private addToMap(position: [number, number], map: Map) {
    const left = position[0] - this.spriteInstance.width / 2;
    const right = position[0] + this.spriteInstance.width / 2;
    const top = position[1] - this.spriteInstance.height / 2;
    const bottom = position[1] + this.spriteInstance.height / 2;
    const NE: LngLat = pointToLngLat(
      new Point(right, top),
      this.config.map.mapWidth,
      this.config.map.mapHeight
    );
    const SW: LngLat = pointToLngLat(
      new Point(left, bottom),
      this.config.map.mapWidth,
      this.config.map.mapHeight
    );
    map.addSource(this.spriteInstance.id, {
      type: 'canvas',
      canvas: this.canvas,
      coordinates: [
        [SW.lng, NE.lat], // NW
        [NE.lng, NE.lat], // NE
        [NE.lng, SW.lat], // SE
        [SW.lng, SW.lat], // SW
      ],
      animate: (this.isAnimated() || this.hasRoute()) && this.isHighFidelity,
    });
    map.addLayer(
      {
        id: this.spriteInstance.id,
        type: 'raster',
        source: this.spriteInstance.id,
        paint: {
          'raster-fade-duration': 0,
        },
      },
      this.spriteInstance.before
    );
    const frameToDisplay = this.spriteSource.frameSequences[
      this.currentSpriteSequence
    ][this.currentSpriteFrame];
    const frameCoords = this.getFrameCoords(frameToDisplay);
    this.drawFrame(frameCoords.x, frameCoords.y);
    const source = map.getSource(this.spriteInstance.id) as CanvasSource;
    return source;
  }

  public isAnimated(): boolean {
    return (
      this.spriteSource.frameSequences[this.currentSpriteSequence].length > 1
    );
  }

  public stepAnimation() {
    this.frameCount += 1;
    if (this.frameCount >= this.spriteSource.framesPerFrame) {
      this.frameCount = 0;
      this.currentSpriteFrame += 1;
      if (
        this.currentSpriteFrame >=
        this.spriteSource.frameSequences[this.currentSpriteSequence].length
      ) {
        this.currentSpriteFrame = 0;
      }
      const frameToDisplay = this.spriteSource.frameSequences[
        this.currentSpriteSequence
      ][this.currentSpriteFrame];
      const frameCoords = this.getFrameCoords(frameToDisplay);
      this.ctx.clearRect(
        0,
        0,
        this.spriteSource.width,
        this.spriteSource.height
      );
      this.drawFrame(frameCoords.x, frameCoords.y);
    }
  }

  private getFrameCoords(
    frameToDisplay: number
  ): {
    x: number;
    y: number;
  } {
    const totalCols = this.spriteSource.image.width / this.spriteSource.width;
    const x = frameToDisplay % totalCols;
    const y = Math.floor(frameToDisplay / totalCols);

    return {
      x,
      y,
    };
  }

  private drawFrame(frameX: number, frameY: number) {
    this.ctx.drawImage(
      this.spriteSource.image,
      frameX * this.spriteSource.width,
      frameY * this.spriteSource.height,
      this.spriteSource.width,
      this.spriteSource.height,
      0,
      0,
      this.spriteSource.width,
      this.spriteSource.height
    );
    // Cheeky script for outputting the canvas contents to the console
    // console.log(
    //   '%c  ',
    //   `font-size:${
    //     this.spriteSource.height
    //   }px; background:url('${this.canvas.toDataURL()}') center / 100% 100%;`
    // );
  }

  public playCanvasSource() {
    if (!this.isHighFidelity) return;
    this.canvasSource.play();
  }

  public pauseCanvasSource() {
    if (!this.isHighFidelity) return;
    this.canvasSource.pause();
  }

  public getCoordinates(): number[][] {
    return this.canvasSource.coordinates;
  }

  public hasRoute() {
    return !!this.spriteInstance.route;
  }

  public stepPosition() {
    if (
      this.routeLength === undefined ||
      this.routeLineString === undefined ||
      this.spriteInstance.route === undefined
    )
      return;
    const curDistance =
      (this.currentRouteStep / this.spriteInstance.route.steps) *
      this.routeLength;
    const { geometry } = along(this.routeLineString, curDistance, {
      units: 'miles',
    });
    if (!geometry) return;
    const point = lngLatToPoint(
      new LngLat(geometry.coordinates[0], geometry.coordinates[1]),
      this.config.map.mapWidth,
      this.config.map.mapHeight
    );

    const left = point.x - this.spriteInstance.width / 2;
    const right = point.x + this.spriteInstance.width / 2;
    const top = point.y - this.spriteInstance.height / 2;
    const bottom = point.y + this.spriteInstance.height / 2;
    const NE: LngLat = pointToLngLat(
      new Point(right, top),
      this.config.map.mapWidth,
      this.config.map.mapHeight
    );
    const SW: LngLat = pointToLngLat(
      new Point(left, bottom),
      this.config.map.mapWidth,
      this.config.map.mapHeight
    );
    this.currentSpriteSequence = this.calculateFrameSequence(
      this.canvasSource.coordinates[1],
      [NE.lng, NE.lat]
    );

    this.canvasSource.setCoordinates([
      [SW.lng, NE.lat], // NW
      [NE.lng, NE.lat], // NE
      [NE.lng, SW.lat], // SE
      [SW.lng, SW.lat], // SW
    ]);

    this.currentRouteStep += 1;
    if (this.currentRouteStep >= this.spriteInstance.route.steps)
      this.currentRouteStep = 0;
  }

  private calculateFrameSequence(oldPos: number[], newPos: number[]): string {
    if (!this.spriteInstance.route) return this.currentSpriteSequence;
    const b = bearing(oldPos, newPos);
    const frameSequenceKey = Object.keys(
      this.spriteInstance.route.bearingSequenceMap
    ).reduce((prev, curr) => {
      return Math.abs(Number(curr) - b) < Math.abs(Number(prev) - b)
        ? curr
        : prev;
    });
    return this.spriteInstance.route.bearingSequenceMap[frameSequenceKey];
  }
}
